import { ActionType, createReducer } from 'typesafe-actions';

import { DashboardPeriodEnum, DataTrendsEnum, GroupByEnum } from '@nimbly-technologies/nimbly-common/lib/enumerators';

import * as actions from './operational.action';
import { OperationalReducerState } from './operationalStore';
import {
	DEFAULT_OPERATIONAL_PAGE_GLOBAL_FILTERS,
	LAST_MONTH_OPERATIONAL_PAGE_GLOBAL_FILTERS,
} from 'pages/dashboardRevamp/dashboardUtils';

export type OperationalAction = ActionType<typeof actions>;

export const initialState: OperationalReducerState = {
	isReportDetailLoading: false,
	isIssueDetailLoading: false,
	isIssuesListLoading: false,
	isReportsListLoading: false,
	isIssueChartLoading: false,
	isReportChartLoading: false,
	errorMessage: '',
	metric: 'report-overview',
	limit: 5,
	/** Filter query information */
	filters: DEFAULT_OPERATIONAL_PAGE_GLOBAL_FILTERS,
	lastMonthFilters: LAST_MONTH_OPERATIONAL_PAGE_GLOBAL_FILTERS,
	groupBy: GroupByEnum.SITE,
	useTrends: false,
	period: DashboardPeriodEnum.QUARTERLY,
	sortBy: 'createdAt',
	sortDirection: 'desc',
	viewBy: 'site',
	reportRCRDetailData: {
		rcr: 0,
		trend: DataTrendsEnum.STABLE,
		dataPoints: [],
	},
	completeReportDetailData: {
		missedPercentage: 0,
		missed: 0,
		pending: 0,
		completed: 0,
		adhoc: 0,
		scheduled: 0,
		rcr: 0,
		total: 0,
	},
	issueOverviewDetailData: {
		irr: 0,
		resolvedIssues: 0,
		openIssuesCount: 0,
		openIssuesPercentage: 0,
		totalIssues: 0,
		issueOpenLastWeekPercentage: 0,
		avgIssueResolutionTime: '',
	},
	issueOverviewLastMonthDetailData: {
		irr: 0,
		resolvedIssues: 0,
		openIssuesCount: 0,
		openIssuesPercentage: 0,
		totalIssues: 0,
		issueOpenLastWeekPercentage: 0,
		avgIssueResolutionTime: '',
	},
	issueChartDataList: [],
	reportChartDataList: [],
	topChartDataList: { labels: [], data: [] },
	topChartIsLoading: false,
	trendsChartIsLoading: false,
	trendsChartDataIssueList: {
		labels: [],
		resolvedIssuesList: [],
		openedIssuesList: [],
		inProgressIssuesList: [],
		inReviewIssuesList: [],
		blockedIssuesList: [],
		totalIssuesList: [],
	},
	trendsChartDataReportList: {
		labels: [],
		completedReportList: [],
		incompleteReportList: [],
		totalReportList: [],
	},
	reportDownloadIsLoading: false,
	reportDownloadData: [],
	reportDownloadGroupBy: GroupByEnum.SITE,
	reportDownloadFieldNames: [],

	reportDownloadTrendsData: [],
	reportDownloadTrendsFieldNames: [],

	// IssueChart data for BasicDashboardPage
	issueChartByCategory: {
		data: null,
		isLoading: false,
	},
	issueChartByQuestionnaire: {
		data: null,
		isLoading: false,
	},

	// SummaryView data for BasicDashboardPage
	summaryViewBySite: {
		reportList: null,
		issueList: null,
		isRCRLoading: false,
		isIRRLoading: false,
	},
	summaryViewByUser: {
		reportList: null,
		issueList: null,
		isRCRLoading: false,
		isIRRLoading: false,
	},
	summaryViewByDepartment: {
		reportList: null,
		issueList: null,
		isRCRLoading: false,
		isIRRLoading: false,
	},

	// Search trigger for coordinated fetches (OperationalPage)
	searchTrigger: 0,
};
export const operationalReducer = createReducer<OperationalReducerState, OperationalAction>(initialState)
	.handleAction(actions.setGroupBy, (state, action) => ({
		...state,
		groupBy: action.payload.groupBy,
	}))
	.handleAction(actions.setPeriod, (state, action) => ({
		...state,
		period: action.payload.period,
	}))
	.handleAction(actions.setTopChartIsLoading, (state, action) => ({
		...state,
		topChartIsLoading: action.payload.isLoading,
	}))
	.handleAction(actions.setTopChartData, (state, action) => {
		const { labels, data } = action.payload;

		return {
			...state,
			topChartDataList: { labels, data },
		};
	})
	.handleAction(actions.setTrendsChartIsLoading, (state, action) => ({
		...state,
		trendsChartIsLoading: action.payload.isLoading,
	}))
	.handleAction(actions.setTrendsChartIssueData, (state, action) => {
		const {
			labels,
			resolvedIssuesList,
			openedIssuesList,
			totalIssuesList,
			inProgressIssuesList,
			inReviewIssuesList,
			blockedIssuesList,
		} = action.payload;

		return {
			...state,
			trendsChartDataIssueList: {
				labels,
				resolvedIssuesList,
				openedIssuesList,
				totalIssuesList,
				inProgressIssuesList,
				inReviewIssuesList,
				blockedIssuesList,
			},
		};
	})
	.handleAction(actions.setTrendsChartReportData, (state, action) => {
		const { labels, completedReportList, incompleteReportList, totalReportList } = action.payload;

		return {
			...state,
			trendsChartDataReportList: { labels, completedReportList, incompleteReportList, totalReportList },
		};
	})
	.handleAction(actions.setReportDownloadViewIsLoading, (state, action) => ({
		...state,
		reportDownloadIsLoading: action.payload.isLoading,
	}))
	.handleAction(actions.setReportDownloadViewData, (state, action) => ({
		...state,
		reportDownloadData: action.payload.data,
	}))
	.handleAction(actions.setReportDownloadViewTrendsData, (state, action) => ({
		...state,
		reportDownloadTrendsData: action.payload.data,
	}))
	.handleAction(actions.setReportDownloadViewTrendsFieldNames, (state, action) => ({
		...state,
		reportDownloadTrendsFieldNames: action.payload.fieldNames,
	}))
	.handleAction(actions.setFilters, (state, action) => ({
		...state,
		filters: action.payload.filters,
	}))
	.handleAction(actions.fetchCompleteReportDetailAsync.request, (state) => ({
		...state,
		isReportDetailLoading: true,
	}))
	.handleAction(actions.fetchCompleteReportDetailAsync.success, (state, action) => {
		return {
			...state,
			completeReportDetailData: action.payload,
			isReportDetailLoading: false,
		};
	})
	.handleAction(actions.fetchCompleteReportDetailAsync.failure, (state, action) => ({
		...state,
		errorMessage: action.payload,
		isReportDetailLoading: false,
	}))
	.handleAction(actions.fetchIssueOverviewDetailAsync.request, (state) => ({
		...state,
		isIssueDetailLoading: true,
	}))
	.handleAction(actions.fetchIssueOverviewDetailAsync.success, (state, action) => {
		return {
			...state,
			issueOverviewDetailData: action.payload,
			isIssueDetailLoading: false,
		};
	})
	.handleAction(actions.fetchIssueOverviewDetailAsync.failure, (state, action) => ({
		...state,
		errorMessage: action.payload,
		isIssueDetailLoading: false,
	}))
	.handleAction(actions.fetchLastMonthIssueOverviewDetailAsync.request, (state) => ({
		...state,
		isIssueDetailLoading: true,
	}))
	.handleAction(actions.fetchLastMonthIssueOverviewDetailAsync.success, (state, action) => {
		return {
			...state,
			issueOverviewLastMonthDetailData: action.payload,
			isIssueDetailLoading: false,
		};
	})
	.handleAction(actions.fetchLastMonthIssueOverviewDetailAsync.failure, (state, action) => ({
		...state,
		errorMessage: action.payload,
		isIssueDetailLoading: false,
	}))
	.handleAction(actions.fetchIssueChartAsync.request, (state) => ({
		...state,
		isIssueChartLoading: true,
	}))
	.handleAction(actions.fetchIssueChartAsync.success, (state, action) => {
		return {
			...state,
			issueChartDataList: action.payload,
			isIssueChartLoading: false,
		};
	})
	.handleAction(actions.fetchIssueChartAsync.failure, (state, action) => ({
		...state,
		errorMessage: action.payload,
		isIssueChartLoading: false,
	}))
	.handleAction(actions.fetchReportChartAsync.request, (state) => ({
		...state,
		isReportChartLoading: true,
	}))
	.handleAction(actions.fetchReportChartAsync.success, (state, action) => {
		return {
			...state,
			reportChartDataList: action.payload,
			isReportChartLoading: false,
		};
	})
	.handleAction(actions.fetchReportChartAsync.failure, (state, action) => ({
		...state,
		errorMessage: action.payload,
		isReportChartLoading: false,
	}))
	// IssueChart by Category handlers
	.handleAction(actions.fetchIssueChartByCategoryAsync.request, (state) => ({
		...state,
		issueChartByCategory: {
			data: null,
			isLoading: true,
		},
	}))
	.handleAction(actions.fetchIssueChartByCategoryAsync.success, (state, action) => ({
		...state,
		issueChartByCategory: {
			data: action.payload,
			isLoading: false,
		},
	}))
	.handleAction(actions.fetchIssueChartByCategoryAsync.failure, (state, action) => ({
		...state,
		issueChartByCategory: {
			data: null,
			isLoading: false,
		},
		errorMessage: action.payload,
	}))
	// IssueChart by Questionnaire handlers
	.handleAction(actions.fetchIssueChartByQuestionnaireAsync.request, (state) => ({
		...state,
		issueChartByQuestionnaire: {
			data: null,
			isLoading: true,
		},
	}))
	.handleAction(actions.fetchIssueChartByQuestionnaireAsync.success, (state, action) => ({
		...state,
		issueChartByQuestionnaire: {
			data: action.payload,
			isLoading: false,
		},
	}))
	.handleAction(actions.fetchIssueChartByQuestionnaireAsync.failure, (state, action) => ({
		...state,
		issueChartByQuestionnaire: {
			data: null,
			isLoading: false,
		},
		errorMessage: action.payload,
	}))
	// SummaryView RCR by Site handlers
	.handleAction(actions.fetchSummaryRCRBySiteAsync.request, (state) => ({
		...state,
		summaryViewBySite: {
			...state.summaryViewBySite,
			isRCRLoading: true,
		},
	}))
	.handleAction(actions.fetchSummaryRCRBySiteAsync.success, (state, action) => ({
		...state,
		summaryViewBySite: {
			...state.summaryViewBySite,
			reportList: action.payload,
			isRCRLoading: false,
		},
	}))
	.handleAction(actions.fetchSummaryRCRBySiteAsync.failure, (state, action) => ({
		...state,
		summaryViewBySite: {
			...state.summaryViewBySite,
			isRCRLoading: false,
		},
		errorMessage: action.payload,
	}))
	// SummaryView IRR by Site handlers
	.handleAction(actions.fetchSummaryIRRBySiteAsync.request, (state) => ({
		...state,
		summaryViewBySite: {
			...state.summaryViewBySite,
			isIRRLoading: true,
		},
	}))
	.handleAction(actions.fetchSummaryIRRBySiteAsync.success, (state, action) => ({
		...state,
		summaryViewBySite: {
			...state.summaryViewBySite,
			issueList: action.payload,
			isIRRLoading: false,
		},
	}))
	.handleAction(actions.fetchSummaryIRRBySiteAsync.failure, (state, action) => ({
		...state,
		summaryViewBySite: {
			...state.summaryViewBySite,
			isIRRLoading: false,
		},
		errorMessage: action.payload,
	}))
	// SummaryView RCR by User handlers
	.handleAction(actions.fetchSummaryRCRByUserAsync.request, (state) => ({
		...state,
		summaryViewByUser: {
			...state.summaryViewByUser,
			isRCRLoading: true,
		},
	}))
	.handleAction(actions.fetchSummaryRCRByUserAsync.success, (state, action) => ({
		...state,
		summaryViewByUser: {
			...state.summaryViewByUser,
			reportList: action.payload,
			isRCRLoading: false,
		},
	}))
	.handleAction(actions.fetchSummaryRCRByUserAsync.failure, (state, action) => ({
		...state,
		summaryViewByUser: {
			...state.summaryViewByUser,
			isRCRLoading: false,
		},
		errorMessage: action.payload,
	}))
	// SummaryView IRR by User handlers
	.handleAction(actions.fetchSummaryIRRByUserAsync.request, (state) => ({
		...state,
		summaryViewByUser: {
			...state.summaryViewByUser,
			isIRRLoading: true,
		},
	}))
	.handleAction(actions.fetchSummaryIRRByUserAsync.success, (state, action) => ({
		...state,
		summaryViewByUser: {
			...state.summaryViewByUser,
			issueList: action.payload,
			isIRRLoading: false,
		},
	}))
	.handleAction(actions.fetchSummaryIRRByUserAsync.failure, (state, action) => ({
		...state,
		summaryViewByUser: {
			...state.summaryViewByUser,
			isIRRLoading: false,
		},
		errorMessage: action.payload,
	}))
	// SummaryView RCR by Department handlers
	.handleAction(actions.fetchSummaryRCRByDepartmentAsync.request, (state) => ({
		...state,
		summaryViewByDepartment: {
			...state.summaryViewByDepartment,
			isRCRLoading: true,
		},
	}))
	.handleAction(actions.fetchSummaryRCRByDepartmentAsync.success, (state, action) => ({
		...state,
		summaryViewByDepartment: {
			...state.summaryViewByDepartment,
			reportList: action.payload,
			isRCRLoading: false,
		},
	}))
	.handleAction(actions.fetchSummaryRCRByDepartmentAsync.failure, (state, action) => ({
		...state,
		summaryViewByDepartment: {
			...state.summaryViewByDepartment,
			isRCRLoading: false,
		},
		errorMessage: action.payload,
	}))
	// SummaryView IRR by Department handlers
	.handleAction(actions.fetchSummaryIRRByDepartmentAsync.request, (state) => ({
		...state,
		summaryViewByDepartment: {
			...state.summaryViewByDepartment,
			isIRRLoading: true,
		},
	}))
	.handleAction(actions.fetchSummaryIRRByDepartmentAsync.success, (state, action) => ({
		...state,
		summaryViewByDepartment: {
			...state.summaryViewByDepartment,
			issueList: action.payload,
			isIRRLoading: false,
		},
	}))
	.handleAction(actions.fetchSummaryIRRByDepartmentAsync.failure, (state, action) => ({
		...state,
		summaryViewByDepartment: {
			...state.summaryViewByDepartment,
			isIRRLoading: false,
		},
		errorMessage: action.payload,
	}))
	// Search trigger for coordinated fetches (OperationalPage)
	.handleAction(actions.setSearchTrigger, (state) => ({
		...state,
		searchTrigger: state.searchTrigger + 1,
	}))
	// TopChart data handlers (OperationalPage)
	.handleAction(actions.fetchTopChartDataAsync.request, (state) => ({
		...state,
		topChartIsLoading: true,
	}))
	.handleAction(actions.fetchTopChartDataAsync.success, (state, action) => {
		const { tab, data } = action.payload;
		const labels = data?.map(({ name }: { name: string }) => name) ?? [];
		const chartData =
			tab === 'Issues'
				? data?.map(({ resolutionRate }: { resolutionRate: number }) => resolutionRate) ?? []
				: data?.map(({ rcr }: { rcr: number }) => rcr) ?? [];
		return {
			...state,
			topChartDataList: { labels, data: chartData },
			topChartIsLoading: false,
		};
	})
	.handleAction(actions.fetchTopChartDataAsync.failure, (state, action) => ({
		...state,
		topChartIsLoading: false,
		errorMessage: action.payload,
	}))
	// Trends data handlers (OperationalPage)
	.handleAction(actions.fetchTrendsDataAsync.request, (state) => ({
		...state,
		trendsChartIsLoading: true,
	}))
	.handleAction(actions.fetchTrendsDataAsync.success, (state, action) => {
		const { tab, data } = action.payload;

		if (tab === 'Issues') {
			const labels: string[] = [];
			const resolvedIssuesList: number[] = [];
			const openedIssuesList: number[] = [];
			const inProgressIssuesList: number[] = [];
			const inReviewIssuesList: number[] = [];
			const blockedIssuesList: number[] = [];
			const totalIssuesList: number[] = [];

			(data ?? []).forEach(
				({
					date,
					resolved,
					open,
					inProgress,
					inReview,
					blocked,
					total,
				}: {
					date: string;
					resolved: number;
					open: number;
					inProgress: number;
					inReview: number;
					blocked: number;
					total: number;
				}) => {
					labels.push(date);
					resolvedIssuesList.push(resolved);
					openedIssuesList.push(open);
					inProgressIssuesList.push(inProgress);
					inReviewIssuesList.push(inReview);
					blockedIssuesList.push(blocked);
					totalIssuesList.push(total);
				},
			);

			return {
				...state,
				trendsChartDataIssueList: {
					labels,
					resolvedIssuesList,
					openedIssuesList,
					totalIssuesList,
					inProgressIssuesList,
					inReviewIssuesList,
					blockedIssuesList,
				},
				trendsChartIsLoading: false,
			};
		} else {
			const labels: string[] = [];
			const completedReportList: number[] = [];
			const incompleteReportList: number[] = [];
			const totalReportList: number[] = [];

			(data ?? []).forEach(
				({
					date,
					completed,
					incomplete,
					total,
				}: {
					date: string;
					completed: number;
					incomplete: number;
					total: number;
				}) => {
					labels.push(date);
					completedReportList.push(completed);
					incompleteReportList.push(incomplete);
					totalReportList.push(total);
				},
			);

			return {
				...state,
				trendsChartDataReportList: { labels, completedReportList, incompleteReportList, totalReportList },
				trendsChartIsLoading: false,
			};
		}
	})
	.handleAction(actions.fetchTrendsDataAsync.failure, (state, action) => ({
		...state,
		trendsChartIsLoading: false,
		errorMessage: action.payload,
	}));
